import type { PlannedTouchPointResponse, TripResponse } from '../../api/routingJobsApi';
import type { TaskInfo, TouchPoint, Trip } from '../../models/types';

const emptyEditPerformance = {
  editPercent: 0, tpPerTrip: 0, wtPerTripKg: 0, distPerTripKm: 0, tpsWithoutTrip: 0,
  noOfTrips: 0, cpk: 0, cpTp: 0, tripsWithTimeBreach: 0, noOfSlotSlippage: 0,
  tpPerTripDelta: 0, wtPerTripKgDelta: 0, distPerTripKmDelta: 0, tpsWithoutTripDelta: 0,
  noOfTripsDelta: 0, cpkDelta: 0, cpTpDelta: 0, tripsWithTimeBreachDelta: 0,
  noOfSlotSlippageDelta: 0, ctoGmv: 0, ctoGmvDelta: 0,
};

export const toTripsTaskInfo = (details: TripResponse[]): TaskInfo | null => {
  if (!details.length) return null;
  const first = details[0];
  const origin = first.plannedRoute.orderedNodes[0];
  return {
    taskId: `trips-${first.dispatchDate}`,
    userId: first.createdBy,
    tenant: first.tenantId,
    status: 'EXECUTION_PREVIEW',
    config: {
      id: first.depotId,
      algoType: 'TRIP_EXECUTION',
      sourceLngLat: origin ? [origin.longitude, origin.latitude] : [77.5946, 12.9716],
      sourceRouteGeometryNodeKey: origin?.nodeKey,
      geoBarriers: [],
    },
    editPerf: { ...emptyEditPerformance, noOfTrips: details.length },
    trips: details.map(toMapTrip),
  };
};

const toMapTrip = (detail: TripResponse): Trip => {
  const origin = detail.plannedRoute.orderedNodes[0];
  const touchPoints = detail.touchPoints.map((point) => toTouchPoint(point, detail.timezone));
  return {
    name: detail.id,
    vehTripNo: 0,
    distKm: Number(detail.metrics.distanceKm) || 0,
    totalWtKg: Number(detail.metrics.weightKg) || 0,
    volCuFt: (Number(detail.metrics.volumeCc) || 0) / 28_316.8466,
    slotTags: detail.slots || [],
    geoMarkers: {
      tripBoundaryLngLats: [],
      tripCenterLngLat: centerOf(touchPoints, origin ? [origin.longitude, origin.latitude] : [77.5946, 12.9716]),
    },
    breachesAndRatios: {
      wtBreachKg: Number(detail.breaches.wtBreachKg) || 0,
      volBreachCuFt: (Number(detail.breaches.volBreachCc) || 0) / 28_316.8466,
      distBreachKm: Number(detail.breaches.distBreachKm) || 0,
      tpBreach: Number(detail.breaches.tpBreach) || 0,
      timeBreachMin: Number(detail.breaches.timeBreachMin) || 0,
      slotBreaches: Number(detail.breaches.slotBreaches) || 0,
      wtToLimit: Number(detail.breaches.wtToLimitRatio) || 0,
      volToLimit: Number(detail.breaches.volToLimitRatio) || 0,
      distToLimit: Number(detail.breaches.distToLimitRatio) || 0,
      tpToLimit: Number(detail.breaches.tpToLimitRatio) || 0,
      timeToLimit: Number(detail.breaches.timeToLimitRatio) || 0,
      distinctZipCodes: [],
    },
    tripCostPerf: {
      cost: Number(detail.metrics.costInRs) || 0,
      cpk: Number(detail.metrics.costPerKm) || 0,
      cpt: Number(detail.metrics.costPerTouchPoint) || 0,
      gmv: Number(detail.metrics.gmvInRs) || 0,
      ctoGmv: Number(detail.metrics.costAsPercentOfGmv) || 0,
    },
    vehDetails: {
      vehId: detail.vehicle.id,
      driverId: detail.driver.id,
      driverName: detail.driver.name,
      contractId: '',
      contractType: '',
    },
    tpDetails: touchPoints,
    tripMeta: {
      status: detail.status,
      loadId: detail.id,
      startEpoch: detail.plannedDepartureAtEpoch,
      endEpoch: detail.plannedReturnAtEpoch,
    },
  };
};

const toTouchPoint = (point: PlannedTouchPointResponse, timezone: string): TouchPoint => ({
  tpId: point.touchPointId,
  routeGeometryNodeKey: point.routeGeometryNodeKey,
  seqNo: point.sequence,
  name: point.buyerName || point.touchPointId,
  address: point.address || '',
  zipCode: point.pinCode || '',
  lngLat: [point.longitude, point.latitude],
  attemptTime: {
    start: minutesInTimezone(point.plannedArrivalAtEpoch, timezone),
    end: minutesInTimezone(point.plannedDepartureAtEpoch, timezone),
  },
  openHoursMinOfDay: [],
  slotTags: point.slotTag ? [point.slotTag] : [],
  timeBreachMin: 0,
  itemSummary: {
    dropWtKg: point.dropWeightKg || 0,
    pickWtKg: 0,
    volCuFt: (point.dropVolumeCc || 0) / 28_316.8466,
    gmv: point.gmvInRs || 0,
    dropItemCount: 0,
    pickItemCount: 0,
    distinctItemTags: [],
  },
  orderNumber: point.touchPointId,
  customerName: point.buyerName,
  generatorName: point.clusterId,
});

const centerOf = (points: TouchPoint[], fallback: [number, number]): [number, number] => {
  if (!points.length) return fallback;
  return [
    points.reduce((sum, point) => sum + point.lngLat[0], 0) / points.length,
    points.reduce((sum, point) => sum + point.lngLat[1], 0) / points.length,
  ];
};

const minutesInTimezone = (epoch: number, timezone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(epoch));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return Number(values.hour || 0) * 60 + Number(values.minute || 0);
};
